// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/hooks/useGuestSession.ts
// 🔧 PURPOSE: Manage guest JWT session and booking data
//
//             UNA sola sessione per tutta l'app: token e prenotazione stanno
//             in uno store di modulo (useSyncExternalStore), non piu' in uno
//             useState per ogni componente. Un accesso fatto dalla finestra
//             della Home, dal biglietto VIP o dall'accesso automatico arriva
//             subito a tutti gli altri (Home, barra delle consegne, foglio
//             delle lingue...), e cosi' l'uscita. Fuori da React la notizia
//             passa con l'evento `nva_guest_session_changed`; le altre schede
//             la ricevono dall'evento `storage` del browser.
//             Caricamento ed errori restano invece di ciascun componente:
//             ogni pagina mostra i suoi nella sua finestra di accesso.
// ============================================

import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { GuestBooking, authenticateGuest, fetchBookingInfoFromApi, isBookingInvalidatedCode } from '../lib/guestApi';
import { applyGuestLanguage } from '../lib/locale';
import { romeDayKey, shiftDayKey } from '../lib/romeDay';
import { tr } from '../i18n';
// Le richieste di accesso automatico dopo il check-in online (in sospeso) e
// il ricordo di quella scaduta, per il login precompilato.
import { CHECKIN_CLAIMS_KEY, CHECKIN_CLAIM_EXPIRED_KEY } from '../lib/checkInClaimStorage';

const STORAGE_KEY = 'nva_guest_session';
const PENDING_TOKEN_KEY = 'nva_pending_login_token';
const EMAIL_VERIFIED_KEY = 'nva_email_verified';

/** L'evento che annuncia un accesso, un'uscita o una prenotazione aggiornata. */
export const GUEST_SESSION_CHANGED_EVENT = 'nva_guest_session_changed';

/** Ogni quanto si controlla la scadenza mentre l'app e' aperta. */
const EXPIRY_CHECK_MS = 60_000;

interface GuestSession {
  token: string;
  booking: GuestBooking;
  loginAt: string;
}

export interface UseGuestSessionReturn {
  isLoggedIn: boolean;
  isLoading: boolean;
  token: string | null;
  booking: GuestBooking | null;
  guestName: string | null;
  error: string | null;
  /**
   * Set when the most recent login attempt was rejected with a
   * booking_* errorCode. The login modal uses this to render a
   * specialised inline message instead of the generic API error.
   */
  errorCode: string | null;
  login: (bookingReference: string, email: string, deviceId?: string | null) => Promise<boolean>;
  loginWithToken: (token: string) => Promise<boolean>;
  logout: () => void;
  refreshSession: () => Promise<boolean>;
  clearError: () => void;
}

// ---- Scadenza ------------------------------------------------------------------

/**
 * La sessione vale fino alla fine del giorno DOPO la partenza, ora di Roma:
 * quel giorno l'ospite vede ancora "Lascia una recensione" e "Prenota di
 * nuovo" (la casa pero' non si comanda piu' dall'orario di check-out:
 * vedi stayOverAt).
 * Prima scadeva alla mezzanotte UTC del giorno di partenza + 1, cioe' dopo
 * una o due ore di "dopo il soggiorno".
 * Il JWT del channel manager dura fino al giorno dopo la partenza alle
 * 23:59:59 (ora del server, UTC): la sessione finisce prima, mai dopo.
 * La data puo' arrivare "AAAA-MM-GG" (/booking/info, gia' in ora di Roma)
 * o ISO (chat/auth): romeDayKey le porta entrambe al giorno di Roma.
 */
export const isSessionExpired = (booking: Pick<GuestBooking, 'checkOut'>, now: Date = new Date()): boolean => {
  if (!booking?.checkOut) return false;
  const checkOutDay = romeDayKey(booking.checkOut);
  const lastDay = checkOutDay ? shiftDayKey(checkOutDay, 1) : '';
  const today = romeDayKey(now);
  if (!lastDay || !today) return false;
  return today > lastDay;
};

// ---- localStorage, sempre protetto (privata, quota, Safari...) -------------------

const safeGet = (key: string): string | null => {
  try {
    return typeof window === 'undefined' ? null : localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Spazio pieno o storage bloccato: la sessione resta in memoria.
  }
};

const safeRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // niente da fare
  }
};

/**
 * Il codice della porta non si salva MAI: chat/auth lo manda dentro la
 * prenotazione, ma l'app lo chiede solo dopo la verifica dell'email e lo
 * tiene in memoria. Nessun componente lo legge dalla sessione.
 */
const withoutAccessCode = (booking: GuestBooking): GuestBooking => {
  if (!booking || !('accessCode' in booking)) return booking;
  const { accessCode: _accessCode, ...rest } = booking as GuestBooking & { accessCode?: unknown };
  return rest as GuestBooking;
};

/**
 * Load session from localStorage (null if missing, unreadable or expired).
 */
const loadSession = (): GuestSession | null => {
  const saved = safeGet(STORAGE_KEY);
  if (!saved) return null;
  try {
    const session: GuestSession = JSON.parse(saved);
    if (!session || typeof session.token !== 'string' || !session.token) {
      safeRemove(STORAGE_KEY);
      return null;
    }
    // Check if session is expired
    if (session.booking && isSessionExpired(session.booking)) {
      removeSessionKeys();
      return null;
    }
    // Sessioni salvate dalle versioni precedenti: il codice stava dentro la
    // prenotazione. Si toglie dal disco al primo caricamento.
    if (session.booking && 'accessCode' in session.booking) {
      session.booking = withoutAccessCode(session.booking);
      safeSet(STORAGE_KEY, JSON.stringify(session));
    }
    return session;
  } catch {
    safeRemove(STORAGE_KEY);
    return null;
  }
};

/**
 * Le chiavi da togliere all'uscita. Anche il token dell'accesso automatico
 * lasciato dalle push (su un'uscita forzata sarebbe morto e riproverebbe a
 * vuoto) e le richieste di accesso dopo il check-in online: chi esce non
 * deve rientrare da solo.
 */
function removeSessionKeys(): void {
  safeRemove(STORAGE_KEY);
  safeRemove(EMAIL_VERIFIED_KEY);
  safeRemove(PENDING_TOKEN_KEY);
  safeRemove(CHECKIN_CLAIMS_KEY);
  safeRemove(CHECKIN_CLAIM_EXPIRED_KEY);
}

// ---- Lo store ----------------------------------------------------------------------

/** undefined = non ancora letta dal disco (si legge al primo uso). */
let current: GuestSession | null | undefined;
const listeners = new Set<() => void>();
let detachGlobal: (() => void) | null = null;

const getSnapshot = (): GuestSession | null => {
  if (current === undefined) current = loadSession();
  return current;
};

const sameSession = (a: GuestSession | null, b: GuestSession | null): boolean =>
  a === b || (!!a && !!b && JSON.stringify(a) === JSON.stringify(b));

/** Aggiorna lo store e avvisa tutti: i componenti React e, con l'evento, il resto. */
function publish(next: GuestSession | null): void {
  const prev = current === undefined ? null : current;
  current = next;
  if (sameSession(prev, next)) return;
  listeners.forEach((listener) => listener());
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(GUEST_SESSION_CHANGED_EVENT, { detail: { loggedIn: !!next } }));
    } catch {
      // CustomEvent assente (browser molto vecchi): React e' gia' aggiornato.
    }
  }
}

/** Scrive la sessione su disco e nello store (loginAt resta quello dell'accesso). */
function writeSession(token: string, booking: GuestBooking, loginAt?: string): GuestSession {
  const session: GuestSession = {
    token,
    booking: withoutAccessCode(booking),
    loginAt: loginAt || new Date().toISOString(),
  };
  safeSet(STORAGE_KEY, JSON.stringify(session));
  publish(session);
  return session;
}

/**
 * Salva la sessione dopo un accesso riuscito, da qualunque parte arrivi
 * (finestra di accesso, link dell'email, push, prenotazione appena fatta,
 * accesso automatico dopo il check-in). Toglie le richieste di accesso
 * automatico ancora in sospeso: ormai si e' dentro.
 */
export const saveSession = (token: string, booking: GuestBooking): void => {
  if (typeof window === 'undefined') return;
  safeRemove(CHECKIN_CLAIMS_KEY);
  safeRemove(CHECKIN_CLAIM_EXPIRED_KEY);
  writeSession(token, booking);
};

/**
 * Clear session from localStorage. Also clears the pending login token
 * stashed by OneSignal push handlers, since on a forced logout (booking
 * invalidated) that token is dead and would just retry-fail the auto-login.
 */
export const clearSession = (): void => {
  if (typeof window === 'undefined') return;
  removeSessionKeys();
  publish(null);
};

/** Scaduta mentre l'app era aperta (o in background): si esce. */
function checkExpiry(): void {
  const session = current === undefined ? null : current;
  if (session?.booking && isSessionExpired(session.booking)) clearSession();
}

/** Un'altra scheda ha fatto accesso o e' uscita: questa si allinea. */
function onStorage(event: StorageEvent): void {
  // key null = localStorage.clear() da un'altra scheda.
  if (event.key !== null && event.key !== STORAGE_KEY) return;
  publish(loadSession());
}

function onVisibility(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') checkExpiry();
}

// ---- Accesso automatico dalla push "check-in collegato" ------------------------------

let pendingTokenInFlight = false;

/**
 * Il token lasciato dalla push checkin_linked (App.tsx) si usa UNA volta
 * sola, per tutta l'app: prima ogni componente provava per conto suo.
 * Se non va, niente messaggio: la finestra di dialogo delle prenotazioni
 * annullate arriva gia' dall'evento globale di guestApi.
 */
async function processPendingToken(): Promise<void> {
  if (typeof window === 'undefined' || pendingTokenInFlight) return;
  const pendingToken = safeGet(PENDING_TOKEN_KEY);
  if (!pendingToken) return;

  // Remove the pending token immediately to prevent duplicate processing
  safeRemove(PENDING_TOKEN_KEY);

  // Skip if already logged in
  if (getSnapshot()?.token) {
    console.log('🔐 Already logged in, skipping pending token');
    return;
  }

  console.log('🔐 Processing pending login token from push notification...');
  pendingTokenInFlight = true;
  try {
    // Force the API call so the channel manager can reject tokens for
    // bookings that have been invalidated since the JWT was minted.
    const result = await fetchBookingInfoFromApi(pendingToken);
    if (result.success && result.booking) {
      saveSession(pendingToken, result.booking);
      applyGuestLanguage(result.booking);
      console.log('✅ Auto-login from push notification successful');
    } else {
      console.warn('⚠️ Auto-login from push notification failed:', result.error);
    }
  } catch (err) {
    console.error('❌ Auto-login from push notification error:', err);
  } finally {
    pendingTokenInFlight = false;
  }
}

function onPendingTokenSet(): void {
  console.log('🔔 Received nva_pending_login_token_set event');
  void processPendingToken();
}

/**
 * Force-logout when any JWT-protected API call returned 403 with a
 * booking_* errorCode. The API helper dispatches the global event; here we
 * just clear the shared session (clearing twice is harmless).
 */
function onBookingInvalidated(): void {
  clearSession();
}

/**
 * I listener globali stanno attaccati finche' c'e' almeno un componente
 * iscritto (in pratica sempre: la barra delle consegne del Layout).
 * UN solo controllo della scadenza per tutta l'app, non uno per componente.
 */
function attachGlobal(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const interval = window.setInterval(checkExpiry, EXPIRY_CHECK_MS);
  window.addEventListener('storage', onStorage);
  window.addEventListener('focus', checkExpiry);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('nva_pending_login_token_set', onPendingTokenSet);
  window.addEventListener('nva_booking_invalidated', onBookingInvalidated);
  return () => {
    window.clearInterval(interval);
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('focus', checkExpiry);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('nva_pending_login_token_set', onPendingTokenSet);
    window.removeEventListener('nva_booking_invalidated', onBookingInvalidated);
  };
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  if (!detachGlobal) {
    detachGlobal = attachGlobal();
    // Appena qualcuno ascolta: una sessione scaduta mentre l'app era chiusa
    // se ne va subito.
    checkExpiry();
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && detachGlobal) {
      detachGlobal();
      detachGlobal = null;
    }
  };
};

/**
 * La sessione corrente, rilegge il disco se nessuno ascolta (senza
 * iscritti l'evento `storage` non arriva).
 */
const readCurrent = (): GuestSession | null => {
  if (listeners.size === 0) publish(loadSession());
  return getSnapshot();
};

/** Il token della sessione salvata, per chi non ha lo hook. */
export const storedGuestToken = (): string | null => readCurrent()?.token || null;

/**
 * Il check-in online risulta fatto nella sessione salvata per QUESTO token.
 * È più fresco del token stesso (il JWT dice com'era quando è stato emesso):
 * la sessione si aggiorna a ogni refresh di MyStay.
 */
export const storedCheckInDone = (token: string): boolean => {
  const session = readCurrent();
  return !!session && session.token === token && !!session.booking?.onlineCheckInCompleted;
};

/**
 * Come storedCheckInDone, ma null quando per QUESTO token non c'è una
 * sessione salvata: chi ha anche il token sa allora a chi credere. Un
 * check-in rifiutato dal gestore torna "da fare" nella sessione, mentre il
 * token continua a dire "fatto".
 */
export const storedCheckInState = (token: string): boolean | null => {
  const session = readCurrent();
  if (!session || session.token !== token || !session.booking) return null;
  return !!session.booking.onlineCheckInCompleted;
};

/**
 * Solo il token, sempre aggiornato: per chi vive fuori dalle pagine e non
 * si rimonta mai (la barra delle consegne, il foglio delle lingue).
 */
export const useGuestToken = (): string | null => {
  const session = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return session?.token || null;
};

/**
 * Il riferimento della prenotazione in sessione (a chi appartiene il
 * carrello degli extra), per la barra in basso: niente richieste, solo lo store.
 */
export const useGuestBookingReference = (): string | null => {
  const session = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return session?.booking?.bookingReference || null;
};

export const useGuestSession = (): UseGuestSessionReturn => {
  const session = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // 🔐 Check for pending login token from push notification (checkin_linked).
  // A ogni montaggio, come prima: il tocco su una push con l'app gia' aperta
  // lascia il token senza evento, e lo raccoglie la pagina che si apre.
  useEffect(() => {
    void processPendingToken();
  }, []);

  /**
   * Login with booking reference and email
   * Optionally sends deviceId for push notifications
   */
  const login = useCallback(async (
    bookingReference: string,
    email: string,
    deviceId?: string | null
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setErrorCode(null);

    try {
      const result = await authenticateGuest(bookingReference, email, deviceId);

      if (result.success && result.token && result.booking) {
        saveSession(result.token, result.booking);
        // L'app parla la lingua scelta al check-in online, dal primo accesso.
        applyGuestLanguage(result.booking);
        return true;
      } else {
        setError(result.error || tr('apiErrors.loginFailed'));
        setErrorCode(result.errorCode || null);
        return false;
      }
    } catch (err) {
      setError(tr('accessCode.errors.connectionError'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login with a pre-generated JWT token (from push notification)
   * Fetches booking info using the token
   */
  const loginWithToken = useCallback(async (token: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setErrorCode(null);

    try {
      // Force a real /booking/info call here (not just a JWT decode) so the
      // server can reject tokens whose booking has been invalidated since the
      // JWT was minted (no-show / cancelled). The decode-only fast path can't
      // see post-issue server state.
      const result = await fetchBookingInfoFromApi(token);

      if (result.success && result.booking) {
        saveSession(token, result.booking);
        applyGuestLanguage(result.booking);
        return true;
      } else {
        setError(result.error || tr('apiErrors.invalidToken'));
        setErrorCode(result.errorCode || null);
        return false;
      }
    } catch (err) {
      setError(tr('accessCode.errors.connectionError'));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout and clear session (for the whole app)
   */
  const logout = useCallback(() => {
    clearSession();
    setError(null);
    setErrorCode(null);
  }, []);

  /**
   * Reset the inline error state (used when the login modal is reopened
   * after a forced logout, to avoid showing stale errorCode messages).
   */
  const clearError = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  /**
   * Refresh session by fetching latest booking info from API
   * Forces API call to get updated check-in status
   * Preserves group booking fields if API doesn't return them
   *
   * Legge la sessione nel momento in cui parte (non quella del render): subito
   * dopo un login(), nella stessa funzione, trova gia' il token nuovo.
   */
  const refreshSession = useCallback(async (): Promise<boolean> => {
    const started = getSnapshot();
    if (!started?.token) return false;

    setIsLoading(true);

    try {
      // Use fetchBookingInfoFromApi to force API call (not JWT decode)
      const result = await fetchBookingInfoFromApi(started.token);

      // Nel frattempo l'ospite puo' essere uscito, o entrato con un'altra
      // prenotazione: la risposta vale solo per lo stesso token.
      const latest = getSnapshot();
      if (!latest || latest.token !== started.token) return false;

      if (result.success && result.booking) {
        // Preserve group booking fields if API doesn't return them
        // This prevents data loss when /api/public/booking/info doesn't include group fields
        const updatedBooking: GuestBooking = {
          ...result.booking,
          isGroupBooking: result.booking.isGroupBooking ?? latest.booking?.isGroupBooking,
          groupBookingId: result.booking.groupBookingId ?? latest.booking?.groupBookingId,
          isNonRefundable: result.booking.isNonRefundable ?? latest.booking?.isNonRefundable,
        };

        writeSession(latest.token, updatedBooking, latest.loginAt);
        return true;
      } else {
        // Token expired (401) or booking invalidated (403 booking_*) — both
        // require a logout. The booking_* path is also triggered globally
        // by the API helper, but we logout here defensively to update local
        // state immediately.
        if (
          result.errorCode === 'SESSION_EXPIRED' ||
          result.error?.includes('expired') ||
          result.error?.includes('401') ||
          isBookingInvalidatedCode(result.errorCode)
        ) {
          logout();
        }
        return false;
      }
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  // Extract first name from full name for display
  const guestName = session?.booking?.guestName
    ? session.booking.guestName.split(' ')[0]
    : null;

  return {
    isLoggedIn: !!session?.token,
    isLoading,
    token: session?.token || null,
    booking: session?.booking || null,
    guestName,
    error,
    errorCode,
    login,
    loginWithToken,
    logout,
    refreshSession,
    clearError,
  };
};
